
import axios, { AxiosInstance } from "axios";


export type MessageType = "user" | "assistant";

export interface ConversationTurn {
  role: "user" | "assistant";
  content: string;
}

export interface AnalyzeRequest {
  query: string;
  max_rows?: number;
  cache?: boolean;
  data_source?: "db" | "excel";
  file_id?: string;
  report_id?: string;
  warehouse_id?: string;
  conversation_history?: ConversationTurn[];
  chat_session_id?: string;
  user_id?: string;
}

export interface PredefinedReport {
  id: string;
  title: string;
  description: string;
  category: string;
  company_id: string | null;
  warehouse_id: string | null;
}

export interface SchemaColumnMeta {
  column: string;
  type: string;
}

export interface UploadResponse {
  file_id: string;
  filename: string;
  sheets: string[];
  schema: Record<string, SchemaColumnMeta[]>;
  row_counts: Record<string, number>;
}

export interface AnalyzeResponse {
  session_id: string;
  chat_session_id: string;
  message: string;
}

export interface ChatSessionData {
  id: string;
  title: string;
  user_id: string | null;
  warehouse_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageData {
  id: string;
  chat_session_id: string;
  role: "user" | "assistant";
  content: string;
  result_json: string | null;
  created_at: string;
}

export interface MetricItem {
  name: string;
  value: string;
  trend: "up" | "down" | "flat";
  delta: string;
}

export interface ChartItem {
  id: string;
  alias: string;
  title: string;
  plotly_json: string; // full Plotly figure JSON string
  chart_type: string;
}

export interface InsightItem {
  title: string;
  description: string;
  impact: "high" | "medium" | "low";
  action: string;
  metric?: string;
}

export interface AnalyticsResult {
  session_id: string;
  query: string;
  summary: string;
  key_metrics: MetricItem[];
  charts: ChartItem[];
  insights: InsightItem[];
  errors: string[];
  node_timings: Record<string, number>;
  elapsed_ms: number;
  clarification_questions: string[];
}

export interface SchemaColumn {
  column: string;
  type: string;
  nullable: boolean;
  primary_key: boolean;
}

export type DatabaseSchema = Record<string, SchemaColumn[]>;

//SSE Event types

export type SSEEventType = "node_start" | "node_done" | "complete" | "error" | "clarification";

export interface NodeStartEvent {
  node: string;
  elapsed_ms: number;
}

export interface NodeDoneEvent {
  node: string;
  elapsed_ms: number;
  [key: string]: unknown;
}

export interface CompleteEvent {
  session_id: string;
  elapsed_ms: number;
}

export interface ClarificationEvent {
  questions: string[];
  elapsed_ms: number;
}

// API client 

class ApiClient {
  private http: AxiosInstance;

  constructor(baseURL = "/api/v1") {
    this.http = axios.create({
      baseURL,
      headers: { "Content-Type": "application/json" },
      timeout: 30_000,
    });

    this.http.interceptors.response.use(
      (r) => r,
      (err) => {
        const msg = err.response?.data?.detail || err.message || "Unknown error";
        return Promise.reject(new Error(msg));
      }
    );
  }

  async analyze(req: AnalyzeRequest): Promise<AnalyzeResponse> {
    const { data } = await this.http.post<AnalyzeResponse>("/analyze", req);
    return data;
  }

  async getResult(sessionId: string): Promise<AnalyticsResult> {
    const { data } = await this.http.get<AnalyticsResult>(`/result/${sessionId}`);
    return data;
  }

  async getSchema(): Promise<DatabaseSchema> {
    const { data } = await this.http.get<DatabaseSchema>("/schema");
    return data;
  }

  async uploadFile(file: File): Promise<UploadResponse> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await this.http.post<UploadResponse>("/upload", form, {
      headers: { "Content-Type": undefined },
      timeout: 60_000,
    });
    return data;
  }

  async getReports(opts?: { companyId?: string; warehouseId?: string }): Promise<PredefinedReport[]> {
    const params: Record<string, string> = {};
    if (opts?.companyId) params.company_id = opts.companyId;
    else if (opts?.warehouseId) params.warehouse_id = opts.warehouseId;
    const { data } = await this.http.get<PredefinedReport[]>("/reports", { params });
    return data;
  }

  async health(): Promise<{ status: string; env: string }> {
    const { data } = await this.http.get("/health");
    return data;
  }

  async listSessions(opts?: { userId?: string; warehouseId?: string }): Promise<ChatSessionData[]> {
    const params: Record<string, string> = {};
    if (opts?.userId) params.user_id = opts.userId;
    if (opts?.warehouseId) params.warehouse_id = opts.warehouseId;
    const { data } = await this.http.get<ChatSessionData[]>("/chat-sessions", { params });
    return data;
  }

  async getSessionMessages(chatSessionId: string): Promise<ChatMessageData[]> {
    const { data } = await this.http.get<ChatMessageData[]>(`/chat-sessions/${chatSessionId}/messages`);
    return data;
  }

  async deleteSession(chatSessionId: string, userId?: string): Promise<void> {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    await this.http.delete(`/chat-sessions/${chatSessionId}`, { params });
  }

  /**
   * Open an SSE stream for agent execution events.
   * Returns a cleanup function — call it to close the connection.
   */
  streamEvents(
    sessionId: string,
    handlers: {
      onNodeStart?: (e: NodeStartEvent) => void;
      onNodeDone?: (e: NodeDoneEvent) => void;
      onComplete?: (e: CompleteEvent) => void;
      onClarification?: (e: ClarificationEvent) => void;
      onError?: (msg: string) => void;
    }
  ): () => void {
    const es = new EventSource(`/api/v1/stream/${sessionId}`);

    es.addEventListener("node_start", (e) => {
      try { handlers.onNodeStart?.(JSON.parse(e.data)); } catch {}
    });
    es.addEventListener("node_done", (e) => {
      try { handlers.onNodeDone?.(JSON.parse(e.data)); } catch {}
    });
    es.addEventListener("clarification", (e) => {
      try { handlers.onClarification?.(JSON.parse(e.data)); } catch {}
      es.close();
    });
    es.addEventListener("complete", (e) => {
      try { handlers.onComplete?.(JSON.parse(e.data)); } catch {}
      es.close();
    });
    es.addEventListener("error", (e: MessageEvent) => {
      try {
        const d = JSON.parse(e.data);
        handlers.onError?.(d.error || "Stream error");
      } catch {
        handlers.onError?.("Connection lost");
      }
      es.close();
    });

    return () => es.close();
  }
}

export const api = new ApiClient();
